import { useEffect, useMemo, useState } from 'react';

import { studentsApi } from '../../services/api';
import type { StudentGradebook as StudentGradebookData } from '../../types';
import { cn } from '../../utils/cn';
import { formatNumber } from '../../utils/gradeSheet';
import {
    CELL_TONE_CLASSES,
    completionTone,
    dateColumnLabel,
    homeworkCellView,
    scoreCellView,
    scoreTone,
    studentHomeworkEntries,
    studentTestEntries,
    type CellView,
} from '../../utils/gradebookView';
import { formatDateKey } from '../../utils/money';
import { PayrollRangePicker, defaultPayrollRange, type PayrollRange } from '../payroll/PayrollRangePicker';
import { EmptyState, LoadingState, Select, useToast } from '../ui';

type Sheet = 'homework' | 'tests';

const SHEETS: Array<{ value: Sheet; label: string }> = [
    { value: 'homework', label: 'Bài tập về nhà' },
    { value: 'tests', label: 'Điểm kiểm tra' },
];

const headCell = 'border-b border-ink-200 bg-ink-50 px-2 py-2 text-center text-xs font-medium text-ink-600';

function ViewCell({ view, className }: { view: CellView; className?: string }) {
    return (
        <td title={view.title} className={cn('whitespace-nowrap px-2 py-2 text-center tabular-nums', CELL_TONE_CLASSES[view.tone], className)}>
            {view.text}
        </td>
    );
}

const classLabel = (item: { name: string; isActive: boolean }) => (item.isActive ? item.name : `${item.name} (đã nghỉ)`);

// Sổ điểm của một học sinh, chỉ để xem. Mỗi lớp tính riêng; "Tất cả lớp" gộp theo ngày.
export function StudentGradebook({ studentId }: { studentId: string }) {
    const { showToast } = useToast();
    const [sheet, setSheet] = useState<Sheet>('homework');
    const [range, setRange] = useState<PayrollRange>(defaultPayrollRange);
    const [classId, setClassId] = useState('all');
    const [data, setData] = useState<StudentGradebookData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setClassId('all');
        setData(null);
    }, [studentId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        studentsApi.getGradebook(studentId, range)
            .then(response => { if (!cancelled) setData(response.data.data); })
            .catch((error: any) => { if (!cancelled) showToast(error?.message || 'Không tải được sổ điểm.', 'error'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [studentId, range, showToast]);

    const classes = data?.classes ?? [];
    const selected = classId === 'all' ? null : classes.find(item => item.id === classId) ?? null;
    const showClass = !selected && classes.length > 1;
    const homework = useMemo(() => (data ? studentHomeworkEntries(data, classId) : []), [data, classId]);
    const tests = useMemo(() => (data ? studentTestEntries(data, classId) : []), [data, classId]);

    return (
        <section className="space-y-3" aria-label="Sổ điểm học sinh">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex rounded-lg border border-ink-200 bg-white p-0.5" role="tablist" aria-label="Sổ điểm">
                    {SHEETS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            role="tab"
                            aria-selected={sheet === option.value}
                            onClick={() => setSheet(option.value)}
                            className={cn(
                                'min-h-8 rounded px-3 text-sm transition-colors',
                                sheet === option.value ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:text-ink-900',
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <PayrollRangePicker value={range} onChange={setRange} />
                    {classes.length > 1 ? (
                        <Select aria-label="Lớp" value={classId} onChange={event => setClassId(event.target.value)} className="w-44">
                            <option value="all">Tất cả lớp ({classes.length})</option>
                            {classes.map(item => <option key={item.id} value={item.id}>{classLabel(item)}</option>)}
                        </Select>
                    ) : null}
                </div>
            </div>

            {loading && !data ? <LoadingState title="Đang tải sổ điểm..." /> : null}
            {!loading && !data ? <EmptyState title="Không tải được sổ điểm" /> : null}
            {data && classes.length === 0 ? <EmptyState title="Học sinh chưa học lớp nào" /> : null}

            {data && classes.length > 0 ? (
                <div className={cn('space-y-3', loading && 'opacity-60')}>
                    {/* Tổng hợp từng lớp trong kỳ; bấm một lớp để xem riêng lớp đó. */}
                    <div className="overflow-x-auto rounded-lg border border-ink-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th scope="col" className={cn(headCell, 'text-left')}>Lớp</th>
                                    <th scope="col" className={headCell}>BTVN</th>
                                    <th scope="col" className={headCell}>% làm bài</th>
                                    <th scope="col" className={headCell}>TB kiểm tra</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-ink-100">
                                {classes.map(item => {
                                    const totals = item.row.homeworkTotals;
                                    const active = item.id === classId;
                                    return (
                                        <tr key={item.id} className={cn(active && 'bg-academy-50')}>
                                            <td className="px-3 py-2">
                                                {classes.length > 1 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setClassId(active ? 'all' : item.id)}
                                                        aria-pressed={active}
                                                        className="text-left font-medium text-ink-900 hover:text-academy-700 hover:underline"
                                                    >
                                                        {classLabel(item)}
                                                    </button>
                                                ) : (
                                                    <span className="font-medium text-ink-900">{classLabel(item)}</span>
                                                )}
                                                {item.subject || item.campusName ? (
                                                    <span className="block text-xs text-ink-500">{[item.subject, item.campusName].filter(Boolean).join(' · ')}</span>
                                                ) : null}
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums text-ink-900">
                                                {totals.assigned > 0
                                                    ? `${formatNumber(totals.correct)}/${formatNumber(totals.completed)}/${formatNumber(totals.assigned)}`
                                                    : '—'}
                                            </td>
                                            <ViewCell view={{
                                                text: totals.completionPercent === null ? '—' : `${formatNumber(totals.completionPercent)}%`,
                                                tone: completionTone(totals.completionPercent),
                                            }} />
                                            <ViewCell view={item.row.average === null
                                                ? { text: '—', tone: 'muted' }
                                                : { text: formatNumber(item.row.average), tone: scoreTone(item.row.average) }} />
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {sheet === 'homework' ? (
                        homework.length === 0 ? (
                            <EmptyState title="Không có buổi học nào trong khoảng này" description="Thử chọn tháng khác." />
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-ink-200">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th scope="col" className={cn(headCell, 'text-left')}>Buổi học</th>
                                            <th scope="col" className={headCell}>Đúng/Đã làm/Được giao</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-ink-100">
                                        {homework.map(entry => (
                                            <tr key={entry.key}>
                                                <td className="px-3 py-2">
                                                    <span className="block text-ink-900">{dateColumnLabel(entry.dateKey).weekday}, {formatDateKey(entry.dateKey)}</span>
                                                    {showClass ? <span className="block text-xs text-ink-500">{entry.className}</span> : null}
                                                </td>
                                                <ViewCell view={homeworkCellView(entry.cell)} />
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : (
                        tests.length === 0 ? (
                            <EmptyState title="Chưa có bài kiểm tra nào trong khoảng này" />
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-ink-200">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th scope="col" className={cn(headCell, 'text-left')}>Bài kiểm tra</th>
                                            <th scope="col" className={headCell}>Hệ số</th>
                                            <th scope="col" className={headCell}>Điểm</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-ink-100">
                                        {tests.map(entry => (
                                            <tr key={entry.key}>
                                                <td className="px-3 py-2">
                                                    <span className="block text-ink-900">{entry.name}</span>
                                                    <span className="block text-xs text-ink-500">
                                                        {formatDateKey(entry.dateKey)}{showClass ? ` · ${entry.className}` : ''}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2 text-center tabular-nums text-ink-700">×{formatNumber(entry.weight)}</td>
                                                <ViewCell view={scoreCellView(entry.cell)} />
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    <p className="text-xs text-ink-500">
                        {sheet === 'homework'
                            ? 'Ô ghi đúng/đã làm/được giao. Vắng tính 0/0/số bài được giao của buổi. “—”: chưa tạo buổi học.'
                            : 'Thang điểm 10. Trung bình tính riêng từng lớp, có nhân hệ số; bài vắng hoặc chưa có điểm không được tính.'}
                        {' '}Chỉ tính các buổi của lớp em đang học, không tính buổi học bù ở lớp khác. Muốn sửa, mở đúng buổi học ở trang Buổi học.
                    </p>
                </div>
            ) : null}
        </section>
    );
}
